// ─────────────────────────────────────────────────────────────────────────────
// notebook_state.ts — Webview-side reactive notebook state
//
// Maintains a lightweight, render-ready copy of the notebook model.
// Processes incoming GrimoireActions (from the host or from local dispatch)
// and keeps block/page/selection state up to date.
//
// Separate from the host model — this is the canvas's source of truth for
// layout and hit-testing, not for persistence.
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';
import type {
  BlockId, PageId, BoundingBox, ArcaneType, ArcaneState,
  CellOutput, CellState, RegionBounds, SectionKind, StrokeId,
} from '@shared/notebook_model';
import { isHiddenSectionKind } from '@shared/notebook_model';
import type { AdventureSubTab } from '@shared/adventure_model';
import { defaultAdventureState } from '@shared/adventure_model';
import type { AdventureState } from '@shared/adventure_model';

// ── Webview block representation ──────────────────────────────────────────────
// Flat union — no Map<> (JSON-serialisable, renderer-friendly)

export interface WvBaseBlock {
  id: BlockId;
  kind: string;
  bbox: BoundingBox;
  rotation: number;    // radians, 0 = upright
  /** Page composition layer (0–5). Default 1 if absent. RETIRED — kept
   *  for backward compatibility with older notebooks; new code uses
   *  the per-content modifier fields below. */
  pageLayer?: number;
  // ── Content modifiers (per-block) ──
  /** If true, block cannot be moved, scaled, rotated, erased, or
   *  edited. Replaces the retired background-layer concept. */
  locked?: boolean;
  /** Membership tag — blocks sharing a groupId move and transform
   *  together. */
  groupId?: string;
  /** Astral aesthetic flag — applied look + fades over `ttlMs`,
   *  then discarded by a system-origin `block.delete`. */
  astral?: { birth: number; ttlMs: number };
}

export interface WvInkBlock extends WvBaseBlock { kind: 'ink'; strokeIds: string[]; }
export interface WvLatexBlock extends WvBaseBlock {
  kind: 'latex';
  strokeIds: string[];
  latexSuggestion?: string;
  latexConfirmed?: string;
  focusMode: boolean;
}
export interface WvCodeBlock extends WvBaseBlock {
  kind: 'code';
  language: string;
  fileRef: string;
  state: CellState;
  outputs: CellOutput[];
}
export type NpcMoveState = 'standing' | 'roaming' | 'going_to' | 'sitting';
export interface WvArcaneBlock extends WvBaseBlock {
  kind: 'arcane';
  arcaneType: ArcaneType;
  arcaneState: ArcaneState;
  glyphSeed: number;
  /** Whether the NPC behind this channel is present and the sigil is "awake". */
  activated: boolean;
  /** NPC activity string (drives shimmer amplitude/speed). */
  npcActivity?: string;
  /** True while the agent is streaming a response — drives processing animation. */
  processing?: boolean;
  /** Ephemeral "say" dialogue — appears next to sigil, disappears on next interaction. */
  ephemeralSay?: string;
  /** Timestamp when ephemeralSay was set (for fade-out timing). */
  ephemeralSayAt?: number;
  // ── Movement ──
  /** Current rendered position (page-local coords). Interpolated toward moveTarget. */
  moveX?: number;
  moveY?: number;
  /** Target position for movement. */
  moveTargetX?: number;
  moveTargetY?: number;
  /** Current movement state. */
  moveState?: NpcMoveState;
  /** Timestamp when current state began. */
  moveStateAt?: number;
  /** Block ID that the NPC is standing near (after response). */
  moveNearBlock?: string;
  // ── Dismiss lifecycle ──
  /** True while the sigil is fading out (farewell animation). */
  dismissing?: boolean;
  /** Timestamp when dismissal started (for fade timing). */
  dismissAt?: number;
}
export interface WvTextStyle {
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  align?: 'left' | 'center' | 'right';
  /** Render the hand-drawn sketch rectangle around the block (default true). */
  showFrame?: boolean;
  /** Agent origin — switches rendering path to cursive/no-frame mode. */
  agentId?: string;
}

/** A handwritten word stored alongside OCR text. */
export interface InkWord {
  /** Original stroke points (page-local, pre-transform). */
  points: Array<{ x: number; y: number; pressure: number }>;
  /** Ink style the word was drawn with. */
  color: string;
  size: number;
  /** Bounding box of the original strokes. */
  srcBBox: { x: number; y: number; w: number; h: number };
  /** Target position/size in block-local coords (where it renders). */
  dstBBox: { x: number; y: number; w: number; h: number };
}
export interface WvTextBlock extends WvBaseBlock {
  kind: 'text';
  content: string;
  fontSize: number;
  textStyle?: WvTextStyle;
  /** Handwritten ink preserved alongside OCR text. */
  inkWords?: InkWord[];
  /** Display mode: handwritten strokes or OCR text. Default 'handwritten'. */
  displayMode?: 'handwritten' | 'ocr';
}
export interface WvFigureBlock extends WvBaseBlock {
  kind: 'figure';
  shapeKind: string;
  vertices: Array<{ x: number; y: number }>;
  edges: Array<{ from: number; to: number; type: 'line' | 'arrow' | 'curve'; controlPoints?: Array<{ x: number; y: number }> }>;
  center?: { x: number; y: number };
  rx?: number;
  ry?: number;
  penSize?: number;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  replacedStrokeIds: string[];
  /** Original stroke kinematic envelope re-mapped onto the clean
   *  parametric geometry. Carries pressure / tilt / timestamp from
   *  the source stroke at each sample so the figure body renders
   *  through the ink layer with the same brush feel as the source.
   *  When present (with `inkStyle`), block_renderer routes the body
   *  through the ink layer; absent → legacy clean-vector path. */
  renderPoints?: import('@shared/notebook_model').StrokePoint[];
  /** Snapshot of the source stroke's InkStyle (tool id, color, size,
   *  opacity, …) at the moment of replacement. Pairs with
   *  renderPoints for ink-layer rendering. */
  inkStyle?: import('@shared/notebook_model').InkStyle;
}

export type WvBlock = WvInkBlock | WvLatexBlock | WvCodeBlock | WvArcaneBlock | WvTextBlock | WvFigureBlock;

// ── Marginalia (wizard responses) ────────────────────────────────────────────

export interface WvMarginNote {
  id: string;
  pageId: PageId;
  anchorBlockId?: BlockId;
  x: number; y: number;
  text: string;
  glyphStyle: 'calm' | 'warning' | 'archaic' | 'dismissive';
  createdAt: number;
}

// ── Page event log (cross-NPC awareness) ──────────────────────────────────────

/** A single NPC event on a page — used so NPCs can reference each other. */
export interface PageEvent {
  /** Which NPC produced this event */
  persona: 'ireth' | 'laura';
  /** What kind of action */
  kind: 'say' | 'write-text' | 'insert-block' | 'displace' | 'draw-glyph' | 'scene';
  /** Human-readable summary (the actual words said, text written, etc.) */
  content: string;
  /** Unix millis */
  timestamp: number;
}

/** Max events kept per page (ring buffer) */
const PAGE_EVENT_LIMIT = 40;

// ── Page state ────────────────────────────────────────────────────────────────

export interface WvPage {
  id: PageId;
  index: number;
  title: string;
  color?: string;
  sectionId?: string;
  isSectionHeader?: boolean;
  /** If false, color and title are locked. Default true. */
  customizable?: boolean;
  densityLevel: number;
  /** Per-page decoration slots — registry ids per sub-slot.
   *    body.justification    — lines / dots / grid / graph / cornell / …
   *    body.graphics         — decorative fills + templates
   *    margins.utility       — functional gutter marks
   *    margins.graphics      — themed ornaments in the margin band
   *    frame                 — body-content frame (plain/simple/thick/…) */
  decorations?: {
    body?: { justification?: string; graphics?: string };
    margins?: { utility?: string; graphics?: string };
    frame?: string;
  };
  blocks: Map<BlockId, WvBlock>;
  marginalia: Map<string, WvMarginNote>;
  arrows: Map<string, WvArrow>;
  /** Cross-NPC event log — recent NPC actions visible to all sigils on this page. */
  events: PageEvent[];
  /** 'folder-landing' for synthetic landing pages (one per folder, including
   *  root); undefined / 'page' for normal user pages. */
  kind?: 'page' | 'folder-landing';
  /** Folder this landing page represents — only set when kind === 'folder-landing'. */
  folderPath?: string;
  /** Two-line description rendered under the title on landing pages. */
  description?: string;
}

/** Section: a named group of pages. */
export interface WvSection {
  id: string;
  title: string;
  description: string;
  color: string;
  pageIds: PageId[];
  headerPageId: PageId;
  kind?: SectionKind;
  /** If false, color and title are locked (built-in sections). Default true. */
  customizable?: boolean;
}

/** Lightweight relation arrow for rendering. */
export interface WvArrow {
  id: string;
  fromId: BlockId;
  toId: BlockId;
  type: string;
  confirmed: boolean;
  label?: string;
}

// ── Focus zone ────────────────────────────────────────────────────────────────

export interface WvFocusZone {
  active: boolean;
  region: RegionBounds | BlockId | null;
  animT: number;
}

// ── Selection state (pure UI, not persisted) ──────────────────────────────────

export interface WvSelection {
  blockIds: Set<BlockId>;
  family: string;   // spell family that performed the selection (for glow colour)
  /** Timestamp of when this selection was created (for animation). */
  birth: number;
}

// ── Handle identifiers ────────────────────────────────────────────────────────

export type HandleId = 'tl' | 't' | 'tr' | 'r' | 'br' | 'b' | 'bl' | 'l' | 'rot' | 'center' | 'del';

export interface HandlePosition {
  id: HandleId;
  x: number;
  y: number;
}

/** Apply a `page.decoration.set` patch to a Page.decorations bag.
 *  Slot is a dotted path:
 *    'body.justification' | 'body.graphics'
 *    'margins.utility'    | 'margins.graphics'
 *    'frame'
 *  `null` clears, anything else writes. Mutates in place. */
export function applyDecorationPatch(
  decos: {
    body?: { justification?: string; graphics?: string };
    margins?: { utility?: string; graphics?: string };
    frame?: string;
  },
  slot: string,
  style: string | null,
): void {
  if (slot === 'frame') {
    if (style === null || style === undefined) delete decos.frame;
    else decos.frame = style;
    return;
  }
  if (slot.startsWith('body.')) {
    const sub = slot.slice('body.'.length) as 'justification' | 'graphics';
    if (sub !== 'justification' && sub !== 'graphics') return;
    if (!decos.body) decos.body = {};
    if (style === null || style === undefined) delete decos.body[sub];
    else decos.body[sub] = style;
    return;
  }
  if (slot.startsWith('margins.')) {
    const sub = slot.slice('margins.'.length) as 'utility' | 'graphics';
    if (sub !== 'utility' && sub !== 'graphics') return;
    if (!decos.margins) decos.margins = {};
    if (style === null || style === undefined) delete decos.margins[sub];
    else decos.margins[sub] = style;
  }
}

/** Compute 8 directional handles + rotation + center handle for a bbox. */
export function computeHandles(
  bbox: { x: number; y: number; width: number; height: number },
  pad = 8,
): HandlePosition[] {
  const { x, y, width: w, height: h } = bbox;
  return [
    { id: 'tl',     x: x - pad,       y: y - pad },
    { id: 't',      x: x + w / 2,     y: y - pad },
    { id: 'tr',     x: x + w + pad,   y: y - pad },
    { id: 'r',      x: x + w + pad,   y: y + h / 2 },
    { id: 'br',     x: x + w + pad,   y: y + h + pad },
    { id: 'b',      x: x + w / 2,     y: y + h + pad },
    { id: 'bl',     x: x - pad,       y: y + h + pad },
    { id: 'l',      x: x - pad,       y: y + h / 2 },
    { id: 'rot',    x: x + w / 2,     y: y - pad - 18 },
    { id: 'center', x: x + w / 2,     y: y + h + pad + 18 },
    { id: 'del',    x: x + w + pad + 14, y: y - pad - 14 },
  ];
}

// ── State class ───────────────────────────────────────────────────────────────

export class WebviewNotebookState {
  private _pages = new Map<PageId, WvPage>();
  private _pageOrder: PageId[] = [];
  private _currentPageIndex = 0;
  private _sections = new Map<string, WvSection>();
  private _sectionOrder: string[] = [];

  private _selection: WvSelection = { blockIds: new Set(), family: 'act', birth: 0 };
  private _focus: WvFocusZone = { active: false, region: null, animT: 0 };

  private _adventure: AdventureState = defaultAdventureState();

  // ── Fold state (mirrored from AgentMemory for rendering) ──────────────
  private _foldBounded = false;
  private _foldEra: 'pre-binding' | 'bound' | 'inhabited' = 'pre-binding';
  private _foldNpcs = new Map<string, { persona: string; mood: string; roomId: string; activity: string }>();
  private _foldUnlockedRooms = new Set<string>();
  private _irethActive = false;
  private _lastKnock: { persona: string; message: string; time: number } | null = null;

  private _listeners: (() => void)[] = [];

  // ── Init ──────────────────────────────────────────────────────────────────

  initialise(data: {
    currentPageIndex: number;
    currentPageId?: string;
    sections?: Array<{
      id: string; title: string; description: string; color: string;
      pageIds: string[]; headerPageId: string; kind?: string;
      customizable?: boolean;
    }>;
    pages: Array<{
      id: string; index: number; title: string; densityLevel: number;
      color?: string; sectionId?: string; isSectionHeader?: boolean;
      kind?: 'page' | 'folder-landing';
      folderPath?: string;
      description?: string;
      blocks?: Array<{
        id: string; kind: string;
        bbox: { x: number; y: number; width: number; height: number };
        rotation?: number;
        pageLayer?: number;
        content?: string; fontSize?: number; textStyle?: Record<string, unknown>;
        arcaneType?: string; arcaneState?: string;
        language?: string; fileRef?: string;
        // Figure block fields
        shapeKind?: string;
        vertices?: Array<{ x: number; y: number }>;
        edges?: Array<{ from: number; to: number; type: string; controlPoints?: Array<{ x: number; y: number }> }>;
        center?: { x: number; y: number };
        rx?: number; ry?: number;
        penSize?: number;
        lineStyle?: string;
        replacedStrokeIds?: string[];
      }>;
      arrows?: Array<{
        id: string; fromId: string; toId: string; type: string; confirmed: boolean;
      }>;
    }>;
  }): void {
    this._pages.clear();
    this._pageOrder = [];
    this._sections.clear();
    this._sectionOrder = [];

    // Restore persisted sections from host
    if (data.sections) {
      for (const s of data.sections) {
        const sec: WvSection = {
          id: s.id,
          title: s.title,
          description: s.description,
          color: s.color,
          pageIds: [...s.pageIds],
          headerPageId: s.headerPageId,
          kind: s.kind as SectionKind | undefined,
          customizable: s.customizable,
        };
        this._sections.set(s.id, sec);
        this._sectionOrder.push(s.id);
      }
    }

    for (const p of data.pages) {
      const page: WvPage = {
        id: p.id, index: p.index,
        title: p.title,
        color: p.color,
        sectionId: p.sectionId,
        isSectionHeader: p.isSectionHeader,
        densityLevel: p.densityLevel,
        decorations: (p as { decorations?: WvPage['decorations'] }).decorations,
        blocks: new Map(),
        marginalia: new Map(),
        arrows: new Map(),
        events: [],
        kind: p.kind,
        folderPath: p.folderPath,
        description: p.description,
      };
      // Restore persisted blocks
      if (p.blocks) {
        for (const b of p.blocks) {
          if (b.kind === 'figure' && b.shapeKind && b.vertices && b.edges) {
            // Full figure block restoration
            const fig: WvFigureBlock = {
              id: b.id,
              kind: 'figure',
              bbox: b.bbox,
              rotation: b.rotation ?? 0,
              pageLayer: b.pageLayer,
              shapeKind: b.shapeKind,
              vertices: b.vertices.map(v => ({ x: v.x, y: v.y })),
              edges: b.edges.map(e => ({
                from: e.from, to: e.to, type: e.type as 'line' | 'arrow' | 'curve',
                controlPoints: e.controlPoints,
              })),
              center: b.center,
              rx: b.rx,
              ry: b.ry,
              penSize: b.penSize,
              lineStyle: b.lineStyle as WvFigureBlock['lineStyle'],
              replacedStrokeIds: b.replacedStrokeIds ?? [],
            };
            page.blocks.set(fig.id, fig);
          } else {
            const wv = makeWvBlock(b.id, b.kind, b.bbox);
            if (b.rotation) { wv.rotation = b.rotation; }
            if (b.pageLayer != null) { (wv as WvBaseBlock).pageLayer = b.pageLayer; }
            if (wv.kind === 'text' && b.content !== undefined) {
              (wv as WvTextBlock).content = b.content;
              if (b.fontSize !== undefined) { (wv as WvTextBlock).fontSize = b.fontSize; }
              if (b.textStyle) { (wv as WvTextBlock).textStyle = b.textStyle as WvTextStyle; }
            }
            if (wv.kind === 'arcane') {
              if (b.arcaneType) { (wv as WvArcaneBlock).arcaneType = b.arcaneType as WvArcaneBlock['arcaneType']; }
              if (b.arcaneState) { (wv as WvArcaneBlock).arcaneState = b.arcaneState as WvArcaneBlock['arcaneState']; }
            }
            if (wv.kind === 'code') {
              if (b.language) { (wv as WvCodeBlock).language = b.language; }
              if (b.fileRef) { (wv as WvCodeBlock).fileRef = b.fileRef; }
            }
            // Preserve driver-level metadata (markdown-body flag, file ref,
            // language) so the block renderer can switch modes per block.
            const bExt = b as Record<string, unknown>;
            for (const key of ['_role', '_language', '_fileRef', '_format'] as const) {
              if (bExt[key] !== undefined) {
                (wv as unknown as Record<string, unknown>)[key] = bExt[key];
              }
            }
            page.blocks.set(wv.id, wv);
          }
        }
      }
      // Restore persisted arrows
      if (p.arrows) {
        for (const a of p.arrows) {
          page.arrows.set(a.id, {
            id: a.id,
            fromId: a.fromId,
            toId: a.toId,
            type: a.type,
            confirmed: a.confirmed,
          });
        }
      }
      this._pages.set(p.id, page);
      this._pageOrder.push(p.id);
    }

    const hadPersistedPages = data.pages.length > 0;

    // Always bootstrap built-in sections if they haven't been added yet
    this._bootstrapWelcome();
    this._bootstrapAdventure();
    this._bootstrapFold();

    if (hadPersistedPages && data.currentPageId) {
      // Restore to exact page by ID
      const idx = this._pageOrder.indexOf(data.currentPageId);
      if (idx !== -1) {
        this._currentPageIndex = idx;
      } else {
        this._currentPageIndex = data.currentPageIndex;
      }
    } else if (hadPersistedPages) {
      this._currentPageIndex = data.currentPageIndex;
    }
    // else: fresh session — no pages yet

    this._notify();
  }

  /** Welcome section removed — intro content now lives in the Codex. */
  private _bootstrapWelcome(): void {
    // No-op: welcome content is rendered by the Codex renderer.
  }

  // ── Adventure bootstrap ────────────────────────────────────────────────────

  private _makeAdvPage(
    id: string, title: string, color: string, isHeader = false,
  ): WvPage {
    return {
      id, index: 0, title, color,
      sectionId: 'sec_adventure',
      isSectionHeader: isHeader,
      densityLevel: 0,
      blocks: new Map(),
      marginalia: new Map(),
      arrows: new Map(),
      events: [],
    };
  }

  private _bootstrapAdventure(): void {
    const secId = 'sec_adventure';
    if (this._sections.has(secId)) return;

    const secColor = '#6a4c93';  // arcane violet
    const pages: WvPage[] = [
      this._makeAdvPage('adv_action',      'Adventure',  secColor, true),
      this._makeAdvPage('adv_bookmarks',   'Bookmarks',  secColor),
      this._makeAdvPage('adv_character',   'Character',  secColor),
      this._makeAdvPage('adv_inventory_0', 'Inventory',  secColor),
      this._makeAdvPage('adv_history',     'History',    secColor),
      this._makeAdvPage('adv_build',       'Build',      secColor),
    ];

    const sec: WvSection = {
      id: secId,
      title: 'Adventure',
      description: 'The OX Journal — Adventure Mode',
      color: secColor,
      pageIds: pages.map(p => p.id),
      headerPageId: pages[0].id,
      kind: 'adventure',
      customizable: false,
    };
    this._sections.set(secId, sec);
    this._sectionOrder.push(secId);

    for (const p of pages) {
      this._pages.set(p.id, p);
      this._pageOrder.push(p.id);
    }

    // Place a user self-identifying arcane sigil on the character page
    const charPage = this._pages.get('adv_character');
    if (charPage && !charPage.blocks.has('user_arcane')) {
      charPage.blocks.set('user_arcane', {
        id: 'user_arcane', kind: 'arcane',
        arcaneType: 'user_sigil', arcaneState: 'open',
        bbox: { x: 150, y: 340, width: 60, height: 60 }, rotation: 0, glyphSeed: 7,
      } as WvBlock);
      charPage.blocks.set('user_arcane_label', {
        id: 'user_arcane_label', kind: 'text',
        content: 'Your Arcane\n\nThis sigil is yours. It marks your identity within the journal.',
        bbox: { x: 60, y: 420, width: 300, height: 70 }, rotation: 0, fontSize: 14,
        textStyle: { color: 'rgba(50,35,15,0.7)' },
      } as WvBlock);
    }
  }

  // ── Fold bootstrap (The Fold — travelling tavern) ──────────────────────────

  private _makeFoldPage(
    id: string, title: string, color: string, isHeader = false,
  ): WvPage {
    return {
      id, index: 0, title, color,
      sectionId: 'sec_fold',
      isSectionHeader: isHeader,
      densityLevel: 0,
      blocks: new Map(),
      marginalia: new Map(),
      arrows: new Map(),
      events: [],
    };
  }

  private _bootstrapFold(): void {
    const secId = 'sec_fold';
    const secColor = '#8B7355';  // warm amber/parchment

    if (!this._sections.has(secId)) {
      const pages: WvPage[] = [
        this._makeFoldPage('fold_hall',           'The Fold',          '#8B7355', true),
        this._makeFoldPage('fold_study',          'Ireth\'s Study',    '#7B6B8A'),
        this._makeFoldPage('fold_kitchen',        'Kitchen',           '#9B8B6B'),
        this._makeFoldPage('fold_ireth_chamber',  'Ireth\'s Chamber',  '#6B5B7A'),
        this._makeFoldPage('fold_laura_room',     'Laura\'s Room',     '#A08060'),
        this._makeFoldPage('fold_tower',          'Tower',             '#7A8B7A'),
        this._makeFoldPage('fold_patio',          'Patio',             '#8B9B7B'),
        this._makeFoldPage('fold_lounge',         'Lounge',            '#9B7B6B'),
        this._makeFoldPage('fold_basement',       'Basement',          '#6B6B7B'),
      ];

      const sec: WvSection = {
        id: secId,
        title: 'The Fold',
        description: 'A travelling tavern folded between pages',
        color: secColor,
        pageIds: pages.map(p => p.id),
        headerPageId: pages[0].id,
        kind: 'fold',
        customizable: true,
      };
      this._sections.set(secId, sec);
      this._sectionOrder.push(secId);

      for (const p of pages) {
        this._pages.set(p.id, p);
        this._pageOrder.push(p.id);
      }
    }

    // Always ensure arcane invocation blocks exist on key fold pages
    // (blocks may already exist from a fresh bootstrap or may have been
    // lost if the host serialised the page without them)
    this._ensureFoldArcane('fold_hall', 'fold_hall_arcane', 'ireth_channel', 42,
      'fold_welcome_text',
      'The Fold\n\nA travelling tavern folded between the pages of your journal. ' +
      'Ireth Vaun tends the house. Laura keeps the records.\n\n' +
      'Double-tap the page to enter wand mode, then draw an upward sweep (↑) over the sigil below to cast.\n' +
      'Triple-tap a sigil directly to activate it.',
      { x: 60, y: 30, width: 500, height: 120 }, { x: 200, y: 180, width: 200, height: 140 });

    this._ensureFoldArcane('fold_laura_room', 'fold_laura_arcane', 'laura_channel', 77,
      'fold_laura_text',
      'Laura\'s Room\n\nBookshelves line every wall. A reading lamp casts warm light over a cluttered desk.\n\n' +
      'Triple-tap the sigil to activate it, or double-tap to enter wand mode and draw a cast gesture.',
      { x: 60, y: 30, width: 500, height: 100 }, { x: 200, y: 160, width: 200, height: 140 });

    this._ensureFoldArcane('fold_study', 'fold_study_arcane', 'ireth_channel', 13,
      'fold_study_text',
      'Ireth\'s Study\n\nQuiet alcove of maps and instruments. Ireth tends the Fold\'s wards from here.\n\n' +
      'Triple-tap the sigil to activate it, or double-tap to enter wand mode and draw a cast gesture.',
      { x: 60, y: 30, width: 500, height: 100 }, { x: 200, y: 160, width: 200, height: 140 });
  }

  /** Ensure a fold page has its arcane channel block and optional description text. */
  private _ensureFoldArcane(
    pageId: string, arcaneId: string, arcaneType: string, seed: number,
    textId: string, textContent: string,
    textBbox: { x: number; y: number; width: number; height: number },
    arcaneBbox: { x: number; y: number; width: number; height: number },
  ): void {
    const pg = this._pages.get(pageId);
    if (!pg) return;
    if (!pg.blocks.has(arcaneId)) {
      pg.blocks.set(arcaneId, {
        id: arcaneId, kind: 'arcane',
        arcaneType, arcaneState: 'open',
        bbox: arcaneBbox, rotation: 0, glyphSeed: seed,
      } as WvBlock);
    }
    if (!pg.blocks.has(textId)) {
      pg.blocks.set(textId, {
        id: textId, kind: 'text', content: textContent,
        bbox: textBbox, rotation: 0, fontSize: 14, textStyle: { color: '#4B0082' },
      } as WvBlock);
    }
  }

  // ── Action processor ──────────────────────────────────────────────────────

  apply(action: GrimoireAction): void {
    switch (action.type) {

      // ── Ink ──────────────────────────────────────────────────────────────
      case 'ink.commit': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        const bounds = strokeBbox(action.points);

        // Try to merge into a nearby existing ink block (spatial proximity grouping)
        // but never merge into a selected block — selection locks its contents
        let merged = false;
        for (const [, b] of page.blocks) {
          if (b.kind !== 'ink') { continue; }
          if (this._selection.blockIds.has(b.id)) { continue; }
          if (bboxNearby(b.bbox, bounds, 20)) {
            // Expand existing block
            (b as WvInkBlock).strokeIds.push(action.strokeId);
            b.bbox = unionBbox(b.bbox, bounds);
            merged = true;
            break;
          }
        }

        if (!merged) {
          const id: BlockId = `${action.pageId}.ink.${action.strokeId}`;
          const block: WvInkBlock = {
            id, kind: 'ink',
            bbox: bounds,
            rotation: 0,
            strokeIds: [action.strokeId],
          };
          page.blocks.set(id, block);
        }
        this._updateDensity(page);
        break;
      }

      case 'ink.erase': {
        const page = this._getPage(action.pageId);
        if (!page || typeof action.target !== 'string') { break; }
        // Remove any ink block that owns only this stroke
        for (const [id, b] of page.blocks) {
          if (b.kind === 'ink' && b.strokeIds.includes(action.target)) {
            if (b.strokeIds.length === 1) {
              page.blocks.delete(id);
            } else {
              (b as WvInkBlock).strokeIds = b.strokeIds.filter(s => s !== action.target);
            }
          }
        }
        this._updateDensity(page);
        break;
      }

      // ── Blocks ────────────────────────────────────────────────────────────
      case 'block.create': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        const block = makeWvBlock(action.blockId, action.kind, action.bbox);
        if (action.pageLayer != null) { (block as WvBaseBlock).pageLayer = action.pageLayer; }
        page.blocks.set(action.blockId, block);
        this._updateDensity(page);
        break;
      }

      case 'block.move': {
        const block = this._findBlock(action.blockId);
        if (!block) { break; }
        block.bbox = {
          x: block.bbox.x + action.dx,
          y: block.bbox.y + action.dy,
          width: block.bbox.width,
          height: block.bbox.height,
        };
        // Also translate figure geometry (vertices, center)
        if (block.kind === 'figure') {
          const fig = block as WvFigureBlock;
          for (const v of fig.vertices) { v.x += action.dx; v.y += action.dy; }
          if (fig.center) { fig.center.x += action.dx; fig.center.y += action.dy; }
          for (const edge of fig.edges) {
            if (edge.controlPoints) {
              for (const cp of edge.controlPoints) { cp.x += action.dx; cp.y += action.dy; }
            }
          }
        }
        break;
      }

      case 'block.setLayer': {
        const block = this._findBlock(action.blockId);
        if (!block) { break; }
        (block as WvBaseBlock).pageLayer = action.pageLayer;
        break;
      }

      case 'block.scale': {
        const block = this._findBlock(action.blockId);
        if (!block) { break; }
        const { pivot, sx, sy } = action;
        block.bbox = {
          x: pivot.x + (block.bbox.x - pivot.x) * sx,
          y: pivot.y + (block.bbox.y - pivot.y) * sy,
          width: block.bbox.width * sx,
          height: block.bbox.height * sy,
        };
        break;
      }

      case 'block.rotate': {
        const block = this._findBlock(action.blockId);
        if (!block) { break; }
        block.rotation = (block.rotation ?? 0) + action.dAngle;
        // Orbit bbox center around the pivot (matters for multi-block selection)
        const bcx = block.bbox.x + block.bbox.width / 2;
        const bcy = block.bbox.y + block.bbox.height / 2;
        const cos = Math.cos(action.dAngle);
        const sin = Math.sin(action.dAngle);
        const nx = action.pivot.x + (bcx - action.pivot.x) * cos - (bcy - action.pivot.y) * sin;
        const ny = action.pivot.y + (bcx - action.pivot.x) * sin + (bcy - action.pivot.y) * cos;
        block.bbox = {
          x: nx - block.bbox.width / 2,
          y: ny - block.bbox.height / 2,
          width: block.bbox.width,
          height: block.bbox.height,
        };
        break;
      }

      case 'block.delete': {
        const page = this._getPage(action.pageId);
        // Protect system-placed fold blocks from deletion
        if (action.blockId.startsWith('fold_') || action.blockId === 'user_arcane' || action.blockId === 'user_arcane_label') break;
        page?.blocks.delete(action.blockId);
        this._selection.blockIds.delete(action.blockId);
        if (page) { this._updateDensity(page); }
        break;
      }

      // ── Content modifiers ────────────────────────────────────────────────
      case 'block.group': {
        // Stamp groupId on every target block. The action's `newGroupId`
        // doubles as the shared group tag — there is no wrapper block.
        const page = this._getPage(action.pageId);
        if (!page) break;
        for (const bid of action.blockIds) {
          const b = page.blocks.get(bid) as WvBaseBlock | undefined;
          if (b) { b.groupId = action.newGroupId; }
        }
        break;
      }

      case 'selection.set': {
        // Poka-yoke: selection is content-only. Reject empty content so
        // we can never render a phantom "selection rectangle around
        // nothing" — all dispatching code must funnel selection through
        // an actual content set.
        const ids = action.blockIds ?? [];
        if (ids.length === 0) {
          console.warn('[selection.set] rejected: empty blockIds (selection must contain content)');
          break;
        }
        this._selection = {
          blockIds: new Set(ids),
          family: action.family ?? 'act',
          birth: performance.now(),
        };
        this._notify();
        break;
      }

      case 'selection.clear': {
        this._selection.blockIds.clear();
        this._selection.birth = 0;
        this._notify();
        break;
      }

      case 'content.modifier.set': {
        const page = this._getPage(action.pageId);
        if (!page) break;
        const { patch } = action;
        for (const t of action.targets) {
          if (t.kind === 'block') {
            const b = page.blocks.get(t.id) as WvBaseBlock | undefined;
            if (!b) continue;
            if (patch.locked  !== undefined) b.locked  = patch.locked;
            if (patch.groupId !== undefined) {
              if (patch.groupId === null) delete b.groupId;
              else b.groupId = patch.groupId;
            }
            if (patch.astral !== undefined) {
              if (patch.astral === null) delete b.astral;
              else b.astral = patch.astral;
            }
          }
          // Stroke modifiers don't live in WvPage — canvas_app patches its
          // InkLayer-side stroke records on the same action.
        }
        break;
      }

      // ── Text ───────────────────────────────────────────────────────────────
      case 'text.update': {
        const block = this._findBlock(action.blockId) as WvTextBlock | undefined;
        if (block?.kind === 'text') { block.content = action.content; }
        break;
      }

      case 'text.style': {
        const block = this._findBlock(action.blockId) as WvTextBlock | undefined;
        if (block?.kind === 'text') {
          const s = block.textStyle ?? {};
          for (const [k, v] of Object.entries(action.style)) {
            (s as Record<string, unknown>)[k] = v;
          }
          block.textStyle = s as WvTextStyle;
          if (typeof action.style.fontSize === 'number') {
            block.fontSize = action.style.fontSize as number;
          }
        }
        break;
      }

      // ── LaTeX overlay ─────────────────────────────────────────────────────
      case 'latex.suggest': {
        const block = this._findBlock(action.blockId) as WvLatexBlock | undefined;
        if (block?.kind === 'latex') { block.latexSuggestion = action.latex; }
        break;
      }

      case 'latex.confirm': {
        const block = this._findBlock(action.blockId) as WvLatexBlock | undefined;
        if (block?.kind === 'latex') {
          block.latexConfirmed = action.latex;
          block.latexSuggestion = undefined;
        }
        break;
      }

      case 'latex.reject': {
        const block = this._findBlock(action.blockId) as WvLatexBlock | undefined;
        if (block?.kind === 'latex') { block.latexSuggestion = undefined; }
        break;
      }

      case 'latex.focus': {
        const block = this._findBlock(action.blockId) as WvLatexBlock | undefined;
        if (block?.kind === 'latex') { block.focusMode = action.focused; }
        break;
      }

      // ── Cell output ───────────────────────────────────────────────────────
      case 'cell.output': {
        const block = this._findBlock(action.blockId) as WvCodeBlock | undefined;
        if (block?.kind === 'code') {
          block.outputs = [...block.outputs, action.output];
          block.state = 'done';
        }
        break;
      }

      // ── Arcane ───────────────────────────────────────────────────────────
      case 'arcane.open': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        const id = action.blockId;
        const existing = page.blocks.get(id);
        const bbox = existing?.bbox ?? { x: 100, y: 100, width: 320, height: 200 };
        const arc: WvArcaneBlock = {
          id, kind: 'arcane',
          bbox,
          rotation: existing?.rotation ?? 0,
          arcaneType: action.arcaneType,
          arcaneState: 'open',
          glyphSeed: pseudoRng(id),
          activated: false,
        };
        page.blocks.set(id, arc);
        break;
      }

      case 'arcane.close': {
        const page = this._getPage(action.pageId);
        const block = page?.blocks.get(action.blockId) as WvArcaneBlock | undefined;
        if (block?.kind === 'arcane') { block.arcaneState = 'sealed'; }
        break;
      }

      // ── Focus ─────────────────────────────────────────────────────────────
      case 'focus.enter':
        this._focus = { active: true, region: action.region, animT: 0 };
        break;

      case 'focus.exit':
        this._focus = { active: false, region: this._focus.region, animT: 0 };
        break;

      // ── Pages ─────────────────────────────────────────────────────────────
      case 'page.turn': {
        const dir = action.direction === 'forward' ? 1 : -1;
        const next = this._currentPageIndex + dir;
        if (next >= 0 && next < this._pageOrder.length) {
          this._currentPageIndex = next;
          this._selection.blockIds.clear();
          this._focus = { active: false, region: null, animT: 0 };
        }
        break;
      }

      case 'page.goto': {
        const idx = this._pageOrder.indexOf(action.pageId);
        if (idx !== -1) {
          this._currentPageIndex = idx;
          this._selection.blockIds.clear();
          this._focus = { active: false, region: null, animT: 0 };
        } else {
          // page.goto with an unknown id means the host thought the page
          // existed but the state tree hasn't learned about it yet. Surface
          // this instead of silently no-op'ing — the symptom was "content
          // index remains unwiped when selecting an entry" because the
          // view mode flipped to 'page' while currentPageIndex stayed put.
          console.warn('[page.goto] unknown pageId:', action.pageId,
            '— page tree out of sync with host');
        }
        break;
      }

      case 'page.create': {
        // Idempotent — skip if page already exists (e.g. action bounced from host)
        if (this._pages.has(action.pageId)) { break; }
        // Section-relative index: position within section's page list
        const pcSec = action.sectionId ? this._sections.get(action.sectionId) : undefined;
        const pcIdx = pcSec ? pcSec.pageIds.length : 0;  // will be added to pageIds after
        // Folder-landing markers ride along on synthetic page.create actions
        // emitted by the lazy host on folder expansion. They aren't part of
        // the formal action schema; we read them off the runtime payload
        // and stash them on the page for the renderer to switch on.
        const ext = action as unknown as Record<string, unknown>;
        const kind = (ext.kind === 'folder-landing' ? 'folder-landing' : 'page') as
          'page' | 'folder-landing';
        const folderPath = typeof ext.folderPath === 'string' ? ext.folderPath : undefined;
        const description = typeof ext.description === 'string' ? ext.description : undefined;
        const page: WvPage = {
          id: action.pageId,
          index: pcIdx,
          title: action.title,
          color: action.color,
          sectionId: action.sectionId,
          isSectionHeader: false,
          densityLevel: 0,
          blocks: new Map(),
          marginalia: new Map(),
          arrows: new Map(),
          events: [],
          kind,
          folderPath,
          description,
        };
        this._pages.set(action.pageId, page);
        this._pageOrder.splice(action.insertAfterIndex + 1, 0, action.pageId);
        // Link page into its section
        if (action.sectionId) {
          const sec = this._sections.get(action.sectionId);
          if (sec) { sec.pageIds.push(action.pageId); }
        }
        break;
      }

      case 'page.delete': {
        const target = this._pages.get(action.pageId);
        if (!target) { break; }
        // Refuse to delete folder-landing pages — folder removal is
        // a separate flow that needs to clean up the folder's child
        // pages too. The UI button is gated to non-folder pages, so
        // this is a defence-in-depth check.
        if (target.kind === 'folder-landing') { break; }
        // Don't delete the last remaining real page — would leave
        // the notebook with no navigable pages.
        if (this._pageOrder.length <= 1) { break; }
        // Pick a sibling to navigate to before we drop the target.
        // Prefer the next page in order; fall back to the previous.
        const idx = this._pageOrder.indexOf(action.pageId);
        const navIdx = idx >= 0
          ? (idx + 1 < this._pageOrder.length ? idx + 1 : idx - 1)
          : -1;
        const navTo = navIdx >= 0 ? this._pageOrder[navIdx] : undefined;
        // Unlink from section.
        if (target.sectionId) {
          const sec = this._sections.get(target.sectionId);
          if (sec) sec.pageIds = sec.pageIds.filter(pid => pid !== action.pageId);
        }
        this._pageOrder.splice(idx, 1);
        this._pages.delete(action.pageId);
        // Re-aim current page to the chosen sibling if we just
        // dropped the active page.
        if (this._currentPageId === action.pageId && navTo) {
          this._currentPageId = navTo;
        }
        break;
      }

      // ── Block link (relation arrows) ─────────────────────────────────────
      case 'block.link': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        page.arrows.set(action.arrowId, {
          id: action.arrowId,
          fromId: action.fromId,
          toId: action.toId,
          type: action.relationType,
          confirmed: action.confirmed,
        });
        break;
      }

      // ── Agent insert ──────────────────────────────────────────────────────
      case 'agent.insert': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        for (const block of action.blocks) {
          const wv = makeWvBlock(block.id, block.kind, block.bbox);
          page.blocks.set(wv.id, wv);
        }
        this._updateDensity(page);
        break;
      }

      // ── Wizard response ───────────────────────────────────────────────────
      case 'wizard.respond': {
        const m = action.marginalia;
        const page = this._getPage(m.pageId);
        if (!page) { break; }
        const noteId = `mn_${Date.now()}`;
        page.marginalia.set(noteId, {
          id: noteId,
          pageId: m.pageId,
          anchorBlockId: m.anchorBlockId,
          x: m.x, y: m.y,
          text: m.text,
          glyphStyle: m.glyphStyle,
          createdAt: Date.now(),
        });
        break;
      }

      // ── Sections ──────────────────────────────────────────────────────────
      case 'section.create': {
        if (this._sections.has(action.sectionId)) { break; }
        const sec: WvSection = {
          id: action.sectionId,
          title: action.title,
          description: '',
          color: action.color,
          pageIds: [action.headerPageId],
          headerPageId: action.headerPageId,
        };
        this._sections.set(action.sectionId, sec);
        this._sectionOrder.push(action.sectionId);

        // Also create the header page
        if (!this._pages.has(action.headerPageId)) {
          const hdrPage: WvPage = {
            id: action.headerPageId,
            index: 0,  // section header = front page
            title: action.title,
            color: action.color,
            sectionId: action.sectionId,
            isSectionHeader: true,
            densityLevel: 0,
            blocks: new Map(),
            marginalia: new Map(),
            arrows: new Map(),
            events: [],
          };
          this._pages.set(action.headerPageId, hdrPage);
          this._pageOrder.splice(action.insertAfterIndex + 1, 0, action.headerPageId);
        }
        break;
      }

      case 'section.update': {
        const sec = this._sections.get(action.sectionId);
        if (!sec) { break; }
        if (action.title !== undefined) {
          sec.title = action.title;
          // Also update the header page title
          const hdr = this._pages.get(sec.headerPageId);
          if (hdr) { hdr.title = action.title; }
        }
        if (action.color !== undefined) {
          sec.color = action.color;
          const hdr = this._pages.get(sec.headerPageId);
          if (hdr) { hdr.color = action.color; }
        }
        if (action.description !== undefined) {
          sec.description = action.description;
        }
        break;
      }

      // ── Adventure ─────────────────────────────────────────────────────────
      case 'adventure.unlock':
        this._adventure.unlocked = true;
        break;

      case 'adventure.env':
        Object.assign(this._adventure.env, action.env);
        break;

      case 'adventure.bookmark.acquire':
        this._adventure.bookmarks.push(action.bookmark);
        break;

      case 'adventure.bookmark.activate': {
        const bm = this._adventure.bookmarks.find(b => b.id === action.bookmarkId);
        if (bm) { bm.state = 'active'; }
        break;
      }

      case 'adventure.bookmark.compose': {
        const bm = this._adventure.bookmarks.find(b => b.id === action.bookmarkId);
        if (bm) {
          const slot = bm.sigils.find(s => s.position === action.position);
          if (slot) { slot.arcaneId = action.arcaneId; }
        }
        break;
      }

      case 'adventure.dialog':
        this._adventure.dialogLog.push({
          speaker: action.speaker,
          text: action.text,
          timestamp: action.timestamp,
          pageId: action.pageId,
        });
        break;

      case 'adventure.inventory.expand': {
        const n = this._adventure.inventoryPageCount;
        const pg = this._makeAdvPage(
          action.newPageId, `Inventory ${n + 1}`, '#6a4c93',
        );
        this._pages.set(pg.id, pg);
        const advSec = this._sections.get('sec_adventure');
        if (advSec) {
          // Insert after last inventory page
          let lastInvIdx = -1;
          for (let i = advSec.pageIds.length - 1; i >= 0; i--) {
            if (advSec.pageIds[i].startsWith('adv_inventory')) {
              lastInvIdx = i; break;
            }
          }
          advSec.pageIds.splice(lastInvIdx + 1, 0, pg.id);
        }
        this._pageOrder.push(pg.id);
        this._adventure.inventoryPageCount++;
        break;
      }

      // ── Action scroll ───────────────────────────────────────────────────
      case 'adventure.block.push':
        this._adventure.actionBlocks.push(action.block);
        break;

      case 'adventure.block.resolve': {
        const blk = this._adventure.actionBlocks.find(b => b.id === action.blockId);
        if (blk) { blk.resolved = true; }
        break;
      }

      case 'adventure.block.select': {
        const blk = this._adventure.actionBlocks.find(b => b.id === action.blockId);
        if (blk?.options) {
          const opt = blk.options.find(o => o.id === action.optionId);
          if (opt) { opt.selected = true; blk.resolved = true; }
        }
        break;
      }

      case 'adventure.block.skip': {
        const blk = this._adventure.actionBlocks.find(b => b.id === action.blockId);
        if (blk) { blk.skipped = true; blk.resolved = true; }
        break;
      }

      case 'adventure.location.move':
        this._adventure.env.location = action.locationId;
        break;

      case 'page.update': {
        const pg = this._pages.get(action.pageId);
        if (!pg) { break; }
        if (action.title !== undefined) { pg.title = action.title; }
        if (action.color !== undefined) { pg.color = action.color; }
        break;
      }

      case 'page.decoration.set': {
        const pg = this._pages.get(action.pageId);
        if (!pg) break;
        if (!pg.decorations) pg.decorations = {};
        const decos = pg.decorations;
        applyDecorationPatch(decos, action.slot, action.style);
        // Tidy empty buckets so the serialised page entry stays small.
        if (decos.body && !decos.body.justification && !decos.body.graphics) {
          delete decos.body;
        }
        if (decos.margins && !decos.margins.utility && !decos.margins.graphics) {
          delete decos.margins;
        }
        if (!decos.body && !decos.margins && !decos.frame) delete pg.decorations;
        break;
      }

      // ── Figures ──────────────────────────────────────────────────────────
      case 'figure.moveVertex': {
        const block = this._findBlock(action.blockId);
        if (!block || block.kind !== 'figure') { break; }
        const fig = block as WvFigureBlock;
        const v = fig.vertices[action.vertexIndex];
        if (!v) { break; }
        v.x += action.dx;
        v.y += action.dy;
        // Recompute bbox from all vertices + center
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const vt of fig.vertices) {
          minX = Math.min(minX, vt.x); minY = Math.min(minY, vt.y);
          maxX = Math.max(maxX, vt.x); maxY = Math.max(maxY, vt.y);
        }
        if (fig.center) {
          const r = Math.max(fig.rx ?? 0, fig.ry ?? 0);
          minX = Math.min(minX, fig.center.x - r); minY = Math.min(minY, fig.center.y - r);
          maxX = Math.max(maxX, fig.center.x + r); maxY = Math.max(maxY, fig.center.y + r);
        }
        if (minX < Infinity) {
          fig.bbox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        }
        break;
      }

      case 'figure.moveControlPoint': {
        const block = this._findBlock(action.blockId);
        if (!block || block.kind !== 'figure') { break; }
        const fig = block as WvFigureBlock;
        const edge = fig.edges[action.edgeIndex];
        if (!edge || !edge.controlPoints) { break; }
        const cp = edge.controlPoints[action.cpIndex];
        if (!cp) { break; }
        cp.x += action.dx;
        cp.y += action.dy;
        break;
      }

      case 'figure.split_edge': {
        const block = this._findBlock(action.blockId);
        if (!block || block.kind !== 'figure') { break; }
        const fig = block as WvFigureBlock;
        const e = fig.edges[action.edgeIndex];
        if (!e) { break; }
        // Splitting a curve / arrow needs more care than a clean
        // midpoint insert (control-point split, head placement). For
        // now skip non-line edges — caller already gates on type.
        if (e.type !== 'line') { break; }
        const newIndex = fig.vertices.length;
        fig.vertices.push({ x: action.x, y: action.y });
        const head = { from: e.from, to: newIndex, type: e.type, controlPoints: e.controlPoints };
        const tail = { from: newIndex, to: e.to, type: e.type, controlPoints: e.controlPoints };
        fig.edges.splice(action.edgeIndex, 1, head, tail);
        break;
      }

      case 'figure.update': {
        const block = this._findBlock(action.blockId);
        if (!block || block.kind !== 'figure') { break; }
        const fig = block as WvFigureBlock;
        if (action.penSize !== undefined) { fig.penSize = action.penSize; }
        if (action.lineStyle !== undefined) { fig.lineStyle = action.lineStyle === 'solid' ? undefined : action.lineStyle; }
        if (action.edgeType !== undefined) {
          const et = action.edgeType;
          if (et === 'double-arrow') {
            // Convert all edges to arrows and add reverse-arrow edges
            const newEdges: typeof fig.edges = [];
            for (const e of fig.edges) {
              newEdges.push({ ...e, type: 'arrow' });
              newEdges.push({ from: e.to, to: e.from, type: 'arrow', controlPoints: e.controlPoints });
            }
            fig.edges = newEdges;
            fig.shapeKind = 'double-arrow';
          } else {
            fig.edges = fig.edges.map(e => ({ ...e, type: et }));
            if (et === 'arrow') { fig.shapeKind = 'arrow'; }
            else if (et === 'line') { fig.shapeKind = 'line'; }
          }
        }
        break;
      }

      case 'figure.create': {
        const page = this._getPage(action.pageId);
        if (!page) { break; }
        const fig: WvFigureBlock = {
          id: action.blockId,
          kind: 'figure',
          bbox: action.bbox,
          rotation: 0,
          shapeKind: action.shapeKind,
          penSize: action.penSize,
          lineStyle: action.lineStyle,
          vertices: action.vertices.map((v: { x: number; y: number }) => ({ x: v.x, y: v.y })),
          edges: action.edges.map((e: { from: string; to: string; type: string; controlPoints?: Array<{ x: number; y: number }> }) => ({
            from: parseInt(e.from.replace('v', ''), 10),
            to: parseInt(e.to.replace('v', ''), 10),
            type: e.type as 'line' | 'arrow' | 'curve',
            controlPoints: e.controlPoints,
          })),
          center: action.center,
          rx: action.rx,
          ry: action.ry,
          replacedStrokeIds: action.replacedStrokeIds ?? [],
          // Kinematic envelope + style snapshot (Batch 6) — when both
          // present, block_renderer routes the body through the ink
          // layer for visual unification with the source stroke.
          renderPoints: action.renderPoints,
          inkStyle: action.inkStyle,
        };
        page.blocks.set(action.blockId, fig);
        // Remove replaced ink strokes from their ink blocks
        for (const sid of fig.replacedStrokeIds) {
          for (const [id, b] of page.blocks) {
            if (b.kind === 'ink' && (b as WvInkBlock).strokeIds.includes(sid)) {
              if ((b as WvInkBlock).strokeIds.length === 1) {
                page.blocks.delete(id);
              } else {
                (b as WvInkBlock).strokeIds = (b as WvInkBlock).strokeIds.filter(s => s !== sid);
              }
            }
          }
        }
        this._updateDensity(page);
        break;
      }

      // ── Agent writes ──────────────────────────────────────────────────────
      case 'agent.write': {
        const page = this._getPage(action.pageId);
        if (!page) break;
        // Create a text block for the agent's written content
        const blockId = `agent_${action.agentId}_${Date.now().toString(36)}`;
        const pos = action.position ?? this._findFreePosition(page);
        const wv = makeWvBlock(blockId, 'text', {
          x: pos.x, y: pos.y, width: 280, height: 40,
        });
        if (wv.kind === 'text') {
          (wv as WvTextBlock).content = action.content;
          (wv as WvTextBlock).textStyle = { agentId: action.agentId };
        }
        page.blocks.set(blockId, wv);
        this._updateDensity(page);
        break;
      }

      case 'agent.rewrite': {
        // Full page replacement — used for major agent-driven page changes
        // For safety, only replace blocks, not metadata
        const page = this._getPage(action.pageId);
        if (!page) break;
        try {
          const parsed = JSON.parse(action.pageJson) as Array<{
            id: string; kind: string;
            bbox: { x: number; y: number; width: number; height: number };
            content?: string;
          }>;
          page.blocks.clear();
          for (const def of parsed) {
            const wv = makeWvBlock(def.id, def.kind as WvBlock['kind'], def.bbox);
            if (wv.kind === 'text' && def.content) {
              (wv as WvTextBlock).content = def.content;
            }
            page.blocks.set(def.id, wv);
          }
          this._updateDensity(page);
        } catch {
          console.warn('[state] agent.rewrite: invalid pageJson');
        }
        break;
      }

      // ── The Fold ──────────────────────────────────────────────────────────
      // Fold actions update webview-side state for rendering; the canonical
      // fold state lives in AgentMemory (IndexedDB).  These actions mirror
      // key transitions so the render loop can show rooms, NPC presence, etc.

      case 'fold.bind':
        // House has been bounded — mark era transition, create rooms
        this._foldBounded = true;
        this._foldEra = 'bound';
        this._bootstrapFold();
        break;

      case 'fold.era':
        this._foldEra = action.era;
        break;

      case 'fold.npc.update': {
        const existing = this._foldNpcs.get(action.persona);
        if (existing) {
          if (action.mood) existing.mood = action.mood;
          if (action.roomId) existing.roomId = action.roomId;
          if (action.activity) existing.activity = action.activity;
        } else {
          this._foldNpcs.set(action.persona, {
            persona: action.persona,
            mood: action.mood ?? 'calm',
            roomId: action.roomId ?? 'hall',
            activity: action.activity ?? 'idle',
          });
        }
        break;
      }

      case 'fold.room.unlock': {
        this._foldUnlockedRooms.add(action.roomId);
        break;
      }

      case 'fold.recoherence':
        // Ireth's first recoherence — mark as active
        this._irethActive = true;
        break;

      case 'fold.knock':
        // Door knock — store last knock for rendering on the hall page
        this._lastKnock = { persona: action.persona, message: action.message, time: Date.now() };
        break;
    }

    this._notify();
  }

  // ── Selection helpers ──────────────────────────────────────────────────────

  // ── Lock-focus mode ──────────────────────────────────────────────────────
  // Default (false): locked content is non-interactive; only unlocked
  // content can be selected. Inverted (true): only locked content can
  // be selected — used by the "Toggle locked" focus mode so the user
  // can edit / unlock page decorations.
  private _lockFocus = false;
  get lockFocus(): boolean { return this._lockFocus; }
  setLockFocus(v: boolean): void {
    if (this._lockFocus === v) return;
    this._lockFocus = v;
    // Switching modes invalidates the current selection — what was
    // selectable a moment ago may no longer be, and vice versa.
    this._selection.blockIds.clear();
    this._notify();
  }

  /** True when a block is interactive under the current focus mode. */
  isSelectable(b: WvBaseBlock): boolean {
    return this._lockFocus ? !!b.locked : !b.locked;
  }

  selectBlock(id: BlockId, family = 'act'): void {
    const page = this.currentPage();
    const blk = page?.blocks.get(id) as WvBaseBlock | undefined;
    if (blk && !this.isSelectable(blk)) return;
    this._selection = { blockIds: new Set([id]), family, birth: performance.now() };
    this._notify();
  }

  selectMultiple(ids: BlockId[], family = 'act'): void {
    const page = this.currentPage();
    const allowed = ids.filter(id => {
      const b = page?.blocks.get(id) as WvBaseBlock | undefined;
      return b ? this.isSelectable(b) : true;
    });
    this._selection = { blockIds: new Set(allowed), family, birth: performance.now() };
    this._notify();
  }

  selectRegion(region: RegionBounds | null, family = 'act'): void {
    if (!region) { this._selection.blockIds.clear(); this._notify(); return; }
    const page = this.currentPage();
    if (!page) { return; }
    const ids = new Set<BlockId>();
    for (const [id, block] of page.blocks) {
      if (!this.isSelectable(block as WvBaseBlock)) continue;
      if (bboxEnclosedByRegion(block.bbox, region)) { ids.add(id); }
    }
    this._selection = { blockIds: ids, family, birth: performance.now() };
    this._notify();
  }

  clearSelection(): void {
    this._selection.blockIds.clear();
    this._selection.birth = 0;
    this._notify();
  }

  /** Find the ink block that owns a given stroke ID, or null. */
  findInkBlockByStroke(strokeId: string): BlockId | null {
    const page = this.currentPage();
    if (!page) return null;
    for (const [id, b] of page.blocks) {
      if (b.kind === 'ink' && (b as WvInkBlock).strokeIds.includes(strokeId)) {
        return id;
      }
    }
    return null;
  }

  /** Compute the merged bounding box of the current selection, or null. */
  selectionBbox(): BoundingBox | null {
    const page = this.currentPage();
    if (!page || this._selection.blockIds.size === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const bid of this._selection.blockIds) {
      const blk = page.blocks.get(bid);
      if (!blk) continue;
      minX = Math.min(minX, blk.bbox.x);
      minY = Math.min(minY, blk.bbox.y);
      maxX = Math.max(maxX, blk.bbox.x + blk.bbox.width);
      maxY = Math.max(maxY, blk.bbox.y + blk.bbox.height);
    }
    if (minX >= Infinity) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  get selection(): WvSelection { return this._selection; }
  get focus(): WvFocusZone { return this._focus; }

  clearFocus(): void {
    this._focus = { active: false, region: null, animT: 0 };
    this._notify();
  }

  tickFocusAnim(): void {
    if (this._focus.active && this._focus.animT < 1) {
      this._focus.animT = Math.min(this._focus.animT + 0.05, 1);
    }
  }

  // ── Section accessors ──────────────────────────────────────────────────────

  sections(): WvSection[] {
    return this._sectionOrder.map(id => this._sections.get(id)!).filter(Boolean);
  }

  getSection(id: string): WvSection | undefined {
    return this._sections.get(id);
  }

  /** Get the section that owns the current page, if any. */
  currentSection(): WvSection | undefined {
    const page = this.currentPage();
    if (!page?.sectionId) { return undefined; }
    return this._sections.get(page.sectionId);
  }

  // ── Adventure accessors ────────────────────────────────────────────────────

  get adventure(): AdventureState { return this._adventure; }

  // ── Layer accessors (retired) ──────────────────────────────────────────────
  //
  // The page-layer system was retired in favour of per-content
  // modifiers (lock / group / astral). The fields + accessors stay
  // as stubs so persistence + a handful of internal call sites
  // ((un)disturbed for now) keep compiling; behaviourally:
  //   - activeLayer/layerVisible/evalAllLayers are inert constants.
  //   - blockLayer / isOnActiveLayer / isBlockVisible all return
  //     "visible / on active layer" so every block participates.
  //   - pageLayer field on stored blocks is read+round-tripped but
  //     no longer drives any runtime behaviour.

  get activeLayer(): number { return 1; }
  set activeLayer(_l: number) { /* retired — no-op */ }
  get layerVisible(): readonly boolean[] { return [true, true, true, true, true, true]; }
  setLayerVisible(_layer: number, _visible: boolean): void { /* retired */ }
  get evalAllLayers(): boolean { return true; }
  set evalAllLayers(_v: boolean) { /* retired */ }

  blockLayer(_b: WvBlock): number { return 1; }
  isOnActiveLayer(_b: WvBlock): boolean { return true; }
  isBlockVisible(_b: WvBlock): boolean { return true; }

  // ── Fold accessors ─────────────────────────────────────────────────────────

  get foldBounded(): boolean { return this._foldBounded; }
  get foldEra(): string { return this._foldEra; }
  get irethActive(): boolean { return this._irethActive; }
  get lastKnock(): { persona: string; message: string; time: number } | null { return this._lastKnock; }

  getNpcPresence(persona: string): { mood: string; roomId: string; activity: string } | undefined {
    return this._foldNpcs.get(persona);
  }

  isRoomUnlocked(roomId: string): boolean {
    return this._foldUnlockedRooms.has(roomId);
  }

  isFoldPage(): boolean {
    return this.currentSection()?.kind === 'fold';
  }

  /** Whether the current page is inside the adventure section. */
  isAdventurePage(): boolean {
    return this.currentSection()?.kind === 'adventure';
  }

  /** Pages that appear in the main navigation (excludes fold, adventure). */
  visiblePages(): WvPage[] {
    const hiddenSectionIds = new Set<string>();
    for (const sec of this._sections.values()) {
      if (isHiddenSectionKind(sec.kind)) hiddenSectionIds.add(sec.id);
    }
    return this.pages().filter(p => {
      if (p.sectionId && hiddenSectionIds.has(p.sectionId)) return false;
      return true;
    });
  }

  /** Sections visible in the main navigation (excludes fold, adventure). */
  visibleSections(): WvSection[] {
    return Array.from(this._sections.values()).filter(s => !isHiddenSectionKind(s.kind));
  }

  /** Index of the current page within visiblePages(). */
  get visiblePageIndex(): number {
    return this.visiblePages().findIndex(p => p.id === this.currentPageId);
  }

  /** Resolve which adventure sub-tab a page ID corresponds to. */
  adventureSubTab(pageId?: string): AdventureSubTab | null {
    const id = pageId ?? this.currentPageId;
    if (id === 'adv_action')    return 'action';
    if (id === 'adv_bookmarks') return 'bookmarks';
    if (id === 'adv_character') return 'character';
    if (id === 'adv_history')   return 'history';
    if (id === 'adv_build')     return 'build';
    if (id.startsWith('adv_inventory')) return 'inventory';
    return null;
  }

  /** Get pages belonging to a given section. */
  sectionPages(sectionId: string): WvPage[] {
    const sec = this._sections.get(sectionId);
    if (!sec) { return []; }
    return sec.pageIds.map(pid => this._pages.get(pid)!).filter(Boolean);
  }

  // ── Hit test ──────────────────────────────────────────────────────────────

  /** Returns the topmost block at (x, y) in the current page, or null. */
  hitTest(x: number, y: number): { id: BlockId; kind: string } | null {
    const page = this.currentPage();
    if (!page) { return null; }
    // Iterate in reverse (last-added = topmost)
    const entries = [...page.blocks.values()].reverse();
    for (const block of entries) {
      // Skip blocks on hidden layers
      if (!this.isBlockVisible(block)) continue;
      const { bbox } = block;
      // Pad thin bboxes so lines/strokes are tappable
      const pad = (block.kind === 'figure' || block.kind === 'ink') ? 8 : 0;
      if (x >= bbox.x - pad && x <= bbox.x + bbox.width + pad &&
          y >= bbox.y - pad && y <= bbox.y + bbox.height + pad) {
        return { id: block.id, kind: block.kind };
      }
    }
    return null;
  }

  /**
   * Test whether a stroke path overlaps an existing arrow.
   * Returns the best-matching arrow if the stroke midpoint is close to the
   * line segment between the two block centres, or null.
   */
  hitTestArrow(
    strokePoints: Array<{ x: number; y: number }>,
  ): WvArrow | null {
    const page = this.currentPage();
    if (!page || strokePoints.length < 3) { return null; }

    let bestArrow: WvArrow | null = null;
    let bestDist = 30; // max proximity threshold (CSS px)

    for (const arrow of page.arrows.values()) {
      const fromBlock = page.blocks.get(arrow.fromId);
      const toBlock = page.blocks.get(arrow.toId);
      if (!fromBlock || !toBlock) { continue; }

      // Arrow line: centre-to-centre
      const ax = fromBlock.bbox.x + fromBlock.bbox.width / 2;
      const ay = fromBlock.bbox.y + fromBlock.bbox.height / 2;
      const bx = toBlock.bbox.x + toBlock.bbox.width / 2;
      const by = toBlock.bbox.y + toBlock.bbox.height / 2;

      // Average distance of stroke midpoints to the arrow line
      const samples = [
        strokePoints[Math.floor(strokePoints.length * 0.25)],
        strokePoints[Math.floor(strokePoints.length * 0.5)],
        strokePoints[Math.floor(strokePoints.length * 0.75)],
      ];
      let totalDist = 0;
      for (const p of samples) {
        totalDist += pointToSegmentDist(p.x, p.y, ax, ay, bx, by);
      }
      const avgDist = totalDist / samples.length;

      if (avgDist < bestDist) {
        bestDist = avgDist;
        bestArrow = arrow;
      }
    }
    return bestArrow;
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  currentPage(): WvPage | null {
    const id = this._pageOrder[this._currentPageIndex];
    return id ? (this._pages.get(id) ?? null) : null;
  }

  get currentPageId(): string {
    return this._pageOrder[this._currentPageIndex] ?? 'unknown';
  }

  get pageCount(): number { return this._pageOrder.length; }
  get pageIndex(): number { return this._currentPageIndex; }

  pages(): WvPage[] {
    return this._pageOrder.map(id => this._pages.get(id)!).filter(Boolean);
  }

  /** Bulk-replace a page's blocks + arrows. Used when the host hydrates a
   *  page lazily (its stub had empty maps; the real contents arrive via
   *  page.load after the user navigates to it). Idempotent — repeated
   *  hydration of the same page just overwrites with fresh data. */
  setPageContents(
    pageId: PageId,
    blocks: Array<{
      id: string; kind: string;
      bbox: { x: number; y: number; width: number; height: number };
      rotation?: number;
      pageLayer?: number;
      content?: string; fontSize?: number; textStyle?: Record<string, unknown>;
      arcaneType?: string; arcaneState?: string;
      language?: string; fileRef?: string;
      shapeKind?: string;
      vertices?: Array<{ x: number; y: number }>;
      edges?: Array<{ from: number; to: number; type: string; controlPoints?: Array<{ x: number; y: number }> }>;
      center?: { x: number; y: number };
      rx?: number; ry?: number;
      penSize?: number;
      lineStyle?: string;
      replacedStrokeIds?: string[];
    }> | undefined,
    arrows: Array<{
      id: string; fromId: string; toId: string; type: string; confirmed: boolean;
    }> | undefined,
  ): void {
    const page = this._pages.get(pageId);
    if (!page) return;
    if (blocks) {
      page.blocks.clear();
      for (const b of blocks) {
        if (b.kind === 'figure' && b.shapeKind && b.vertices && b.edges) {
          const fig: WvFigureBlock = {
            id: b.id,
            kind: 'figure',
            bbox: b.bbox,
            rotation: b.rotation ?? 0,
            pageLayer: b.pageLayer,
            shapeKind: b.shapeKind,
            vertices: b.vertices.map(v => ({ x: v.x, y: v.y })),
            edges: b.edges.map(e => ({
              from: e.from, to: e.to, type: e.type as 'line' | 'arrow' | 'curve',
              controlPoints: e.controlPoints,
            })),
            center: b.center,
            rx: b.rx,
            ry: b.ry,
            penSize: b.penSize,
            lineStyle: b.lineStyle as WvFigureBlock['lineStyle'],
            replacedStrokeIds: b.replacedStrokeIds ?? [],
          };
          page.blocks.set(fig.id, fig);
        } else {
          const wv = makeWvBlock(b.id, b.kind, b.bbox);
          if (b.rotation) { wv.rotation = b.rotation; }
          if (b.pageLayer != null) { (wv as WvBaseBlock).pageLayer = b.pageLayer; }
          if (wv.kind === 'text' && b.content !== undefined) {
            (wv as WvTextBlock).content = b.content;
            if (b.fontSize !== undefined) { (wv as WvTextBlock).fontSize = b.fontSize; }
            if (b.textStyle) { (wv as WvTextBlock).textStyle = b.textStyle as WvTextStyle; }
          }
          if (wv.kind === 'arcane') {
            if (b.arcaneType) { (wv as WvArcaneBlock).arcaneType = b.arcaneType as WvArcaneBlock['arcaneType']; }
            if (b.arcaneState) { (wv as WvArcaneBlock).arcaneState = b.arcaneState as WvArcaneBlock['arcaneState']; }
          }
          if (wv.kind === 'code') {
            if (b.language) { (wv as WvCodeBlock).language = b.language; }
            if (b.fileRef) { (wv as WvCodeBlock).fileRef = b.fileRef; }
          }
          const bExt = b as Record<string, unknown>;
          for (const key of ['_role', '_language', '_fileRef', '_format'] as const) {
            if (bExt[key] !== undefined) {
              (wv as unknown as Record<string, unknown>)[key] = bExt[key];
            }
          }
          page.blocks.set(wv.id, wv);
        }
      }
    }
    if (arrows) {
      page.arrows.clear();
      for (const a of arrows) {
        page.arrows.set(a.id, {
          id: a.id, fromId: a.fromId, toId: a.toId, type: a.type, confirmed: a.confirmed,
        });
      }
    }
  }

  /** Push a cross-NPC event onto a page's event log (ring buffer). */
  pushPageEvent(pageId: PageId, event: PageEvent): void {
    const page = this._pages.get(pageId);
    if (!page) return;
    page.events.push(event);
    if (page.events.length > PAGE_EVENT_LIMIT) {
      page.events.splice(0, page.events.length - PAGE_EVENT_LIMIT);
    }
  }

  /** Get recent events on a page, optionally excluding a persona. */
  getPageEvents(pageId: PageId, excludePersona?: string): PageEvent[] {
    const page = this._pages.get(pageId);
    if (!page) return [];
    if (excludePersona) {
      return page.events.filter(e => e.persona !== excludePersona);
    }
    return page.events;
  }

  // ── Change listeners ──────────────────────────────────────────────────────

  onChange(fn: () => void): () => void {
    this._listeners.push(fn);
    return () => { this._listeners = this._listeners.filter(l => l !== fn); };
  }

  private _notify(): void {
    for (const fn of this._listeners) { fn(); }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private _getPage(pageId: PageId): WvPage | null {
    return this._pages.get(pageId) ?? null;
  }

  private _findBlock(blockId: BlockId): WvBlock | null {
    const page = this.currentPage();
    return page?.blocks.get(blockId) ?? null;
  }

  private _updateDensity(page: WvPage): void {
    // Simple density: fraction of page area covered by block bboxes
    const PAGE_AREA = 595 * 840;  // A4-ish reference
    let covered = 0;
    for (const b of page.blocks.values()) { covered += b.bbox.width * b.bbox.height; }
    page.densityLevel = Math.min(covered / PAGE_AREA, 1);
  }

  /** Find a free position on the page for an agent-placed block. */
  private _findFreePosition(page: WvPage): { x: number; y: number } {
    // Start in the margin area (lower-right of the page content zone)
    const baseX = 40;
    let baseY = 60;
    // Walk down to find a Y that doesn't overlap existing blocks
    for (const b of page.blocks.values()) {
      const bottom = b.bbox.y + b.bbox.height;
      if (bottom > baseY) baseY = bottom + 12;
    }
    // Clamp to page bounds (A4-ish)
    if (baseY > 780) baseY = 780;
    return { x: baseX, y: baseY };
  }
}

// ── Utilities ──────────────────────────────────────────────────────────────────

function makeWvBlock(id: BlockId, kind: string, bbox: BoundingBox): WvBlock {
  switch (kind) {
    case 'latex': return { id, kind: 'latex', bbox, rotation: 0, strokeIds: [], focusMode: false };
    case 'code':  return { id, kind: 'code',  bbox, rotation: 0, language: 'python', fileRef: '', state: 'idle', outputs: [] };
    case 'arcane': return { id, kind: 'arcane', bbox, rotation: 0, arcaneType: 'ireth_channel', arcaneState: 'sealed', glyphSeed: pseudoRng(id) };
    case 'text':  return { id, kind: 'text', bbox, rotation: 0, content: '', fontSize: 14, displayMode: 'ocr' };
    case 'figure': return { id, kind: 'figure', bbox, rotation: 0, shapeKind: 'polygon', vertices: [], edges: [], replacedStrokeIds: [] };
    default:      return { id, kind: 'ink',   bbox, rotation: 0, strokeIds: [] };
  }
}

function strokeBbox(points: Array<{ x: number; y: number }>): BoundingBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) { minX = p.x; } if (p.y < minY) { minY = p.y; }
    if (p.x > maxX) { maxX = p.x; } if (p.y > maxY) { maxY = p.y; }
  }
  const pad = 4;
  return { x: minX - pad, y: minY - pad, width: (maxX - minX) + pad * 2, height: (maxY - minY) + pad * 2 };
}

function bboxOverlapsRegion(bbox: BoundingBox, region: RegionBounds): boolean {
  const r = region.bbox;
  return !(bbox.x > r.x + r.width || bbox.x + bbox.width < r.x ||
           bbox.y > r.y + r.height || bbox.y + bbox.height < r.y);
}

/**
 * Check if a block's center is enclosed by the lasso polygon.
 * Falls back to bbox-containment if the polygon has too few points.
 */
export function bboxEnclosedByRegion(bbox: BoundingBox, region: RegionBounds): boolean {
  const cx = bbox.x + bbox.width / 2;
  const cy = bbox.y + bbox.height / 2;

  // Polygon-aware: point-in-polygon on the block centre.  Centre-inside
  // is the cheapest and most-intuitive proxy for "≥ 50 % enclosed".
  if (region.points && region.points.length >= 3) {
    return pointInPolygon(cx, cy, region.points);
  }

  // Bbox-only path: a block is "selected" if it's centre is inside the
  // region OR ≥ 50 % of its area overlaps the region.  Centre-inside
  // catches small blocks at the edge of the lasso; area overlap catches
  // big blocks that the lasso went around.  Either condition wins.
  const r = region.bbox;
  if (cx >= r.x && cx <= r.x + r.width && cy >= r.y && cy <= r.y + r.height) {
    return true;
  }
  const ix = Math.max(0, Math.min(bbox.x + bbox.width, r.x + r.width) - Math.max(bbox.x, r.x));
  const iy = Math.max(0, Math.min(bbox.y + bbox.height, r.y + r.height) - Math.max(bbox.y, r.y));
  const overlapArea = ix * iy;
  const blockArea = Math.max(1, bbox.width * bbox.height);
  return overlapArea / blockArea >= 0.5;
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(px: number, py: number, poly: Array<{ x: number; y: number }>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if (((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Check if two bboxes are within `gap` pixels of each other. */
function bboxNearby(a: BoundingBox, b: BoundingBox, gap: number): boolean {
  return !(a.x - gap > b.x + b.width  || a.x + a.width  + gap < b.x ||
           a.y - gap > b.y + b.height || a.y + a.height + gap < b.y);
}

/** Return the smallest bbox containing both inputs. */
function unionBbox(a: BoundingBox, b: BoundingBox): BoundingBox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right  = Math.max(a.x + a.width,  b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

function pseudoRng(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}

/** Perpendicular distance from point (px,py) to segment (ax,ay)→(bx,by). */
function pointToSegmentDist(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) { return Math.hypot(px - ax, py - ay); }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}
